import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const ENV_API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost:8000";
const API_BASE = `${ENV_API_BASE_URL}/api/testimonials`;

const emptyForm = {
  name: '',
  location: '',
  quote: '',
  image: null,
  rating: 5,
  order: 0,
  isActive: true,
};

const resolveAssetUrl = (value) => {
  if (!value) return '';
  if (/^https?:\/\//i.test(value)) return value;
  if (value.startsWith('/uploads/')) return `${ENV_API_BASE_URL}${value}`;
  if (value.startsWith('uploads/')) return `${ENV_API_BASE_URL}/${value}`;
  return `${ENV_API_BASE_URL}/uploads/${value}`;
};

// ── API helpers ──────────────────────────────────────────────────────────────
const api = {
  getAll: async () => (await axios.get(API_BASE)).data,
  create: async (body) =>
    (
      await axios.post(API_BASE, body, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
    ).data,
  update: async (id, body) =>
    (
      await axios.put(`${API_BASE}/${id}`, body, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
    ).data,
  remove: async (id) => (await axios.delete(`${API_BASE}/${id}`)).data,
};

export default function TestimonialsAdmin() {
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [apiError, setApiError] = useState('');

  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [removeImage, setRemoveImage] = useState(false);
  const [errors, setErrors] = useState({});
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [toast, setToast] = useState(null);

  // ── Fetch all ──────────────────────────────────────────────────────────────
  const fetchAll = useCallback(async () => {
    setLoading(true);
    setApiError('');
    try {
      const res = await api.getAll();
      if (res.success) setTestimonials(res.data);
      else throw new Error(res.message);
    } catch {
      setApiError('Could not connect to server. Make sure the backend is running on port 8000.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // ── Toast ──────────────────────────────────────────────────────────────────
  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  };

  // ── Validate ───────────────────────────────────────────────────────────────
  const validate = () => {
    const e = {};
    if (!formData.name.trim()) e.name = 'Name is required';
    if (!formData.location.trim()) e.location = 'Location is required';
    if (!formData.quote.trim()) e.quote = 'Quote is required';
    else if (formData.quote.trim().length > 200) e.quote = 'Quote must be under 200 characters';
    if (Number(formData.rating) < 1 || Number(formData.rating) > 5) e.rating = 'Rating must be between 1 and 5';
    return e;
  };

  // ── Handlers ───────────────────────────────────────────────────────────────
  const closeForm = () => {
    setFormData(emptyForm);
    setEditingId(null);
    setErrors({});
    setImagePreview('');
    setRemoveImage(false);
    setShowForm(false);
  };

  const handleCreate = () => {
    setFormData(emptyForm);
    setEditingId(null);
    setErrors({});
    setImagePreview('');
    setRemoveImage(false);
    setShowForm(true);
  };

  const handleEdit = (item) => {
    setFormData({
      name: item.name || '',
      location: item.location || '',
      quote: item.quote || '',
      image: null,
      rating: item.rating || 5,
      order: item.order ?? 0,
      isActive: item.isActive !== false,
    });
    setEditingId(item._id);
    setErrors({});
    setImagePreview(resolveAssetUrl(item.image));
    setRemoveImage(false);
    setShowForm(true);
  };

  const handleFileChange = (file) => {
    if (!file) return;
    setFormData((prev) => ({ ...prev, image: file }));
    setImagePreview(URL.createObjectURL(file));
    setRemoveImage(false);
  };

  const handleSave = async () => {
    const e = validate();
    if (Object.keys(e).length > 0) { setErrors(e); return; }
    setSaving(true);
    try {
      const payload = new FormData();
      payload.append('name', formData.name.trim());
      payload.append('location', formData.location.trim());
      payload.append('quote', formData.quote.trim());
      payload.append('rating', String(formData.rating));
      payload.append('order', String(formData.order ?? 0));
      payload.append('isActive', String(formData.isActive));
      if (formData.image instanceof File) {
        payload.append('image', formData.image);
      } else if (removeImage) {
        payload.append('removeImage', 'true');
      }

      const res = editingId ? await api.update(editingId, payload) : await api.create(payload);
      if (!res.success) throw new Error(res.message);
      await fetchAll();
      closeForm();
      showToast(editingId ? 'Testimonial updated!' : 'Testimonial created!');
    } catch (err) {
      showToast(err.message || 'Something went wrong', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteConfirm) return;
    try {
      const res = await api.remove(deleteConfirm._id);
      if (!res.success) throw new Error(res.message);
      await fetchAll();
      setDeleteConfirm(null);
      showToast('Testimonial deleted.');
    } catch (err) {
      showToast(err.message || 'Delete failed', 'error');
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: undefined }));
  };

  const averageRating = testimonials.length
    ? (
        testimonials.reduce((sum, item) => sum + Number(item.rating || 0), 0) /
        testimonials.length
      ).toFixed(1)
    : '0.0';

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,500;0,700;1,500&family=Lato:wght@300;400;700&display=swap');

        *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

        :root {
          --bg:           #f7f5f0;
          --surface:      #ffffff;
          --border:       #e4ddd3;
          --border-soft:  #ede8e1;
          --text-primary: #1c1a16;
          --text-secondary:#6b6355;
          --text-muted:   #a89f93;
          --accent:       #7c5c3a;
          --accent-light: #f0e8de;
          --accent-hover: #5e4428;
          --danger:       #b84040;
          --danger-bg:    #fdf2f2;
          --success:      #3a7c5c;
          --gold:         #c9a96e;
          --shadow-sm:    0 1px 4px rgba(0,0,0,0.06);
          --shadow-md:    0 4px 20px rgba(0,0,0,0.08);
          --shadow-lg:    0 16px 56px rgba(0,0,0,0.14);
        }

        .ta-page { font-family: 'Lato', sans-serif; background: var(--bg); min-height: 100vh; color: var(--text-primary); }

        /* TOP BAR */
        .ta-topbar { background: var(--surface); border-bottom: 1px solid var(--border); padding: 0 40px; height: 58px; display: flex; align-items: center; gap: 8px; box-shadow: var(--shadow-sm); }
        .ta-topbar-logo { font-family: 'Playfair Display', serif; font-size: 17px; font-weight: 700; color: var(--accent); }
        .ta-topbar-sep { color: var(--border); }
        .ta-topbar-page { font-size: 13px; color: var(--text-muted); }

        /* HERO */
        .ta-hero { background: var(--surface); border-bottom: 1px solid var(--border); padding: 52px 40px 44px; }
        .ta-hero-inner { max-width: 1160px; margin: 0 auto; display: flex; align-items: flex-end; justify-content: space-between; gap: 28px; flex-wrap: wrap; }
        .ta-eyebrow { font-size: 11px; text-transform: uppercase; letter-spacing: 2.5px; color: var(--accent); font-weight: 700; margin-bottom: 12px; display: flex; align-items: center; gap: 8px; }
        .ta-eyebrow::before { content:''; width: 24px; height: 2px; background: var(--accent); border-radius: 2px; }
        .ta-heading { font-family: 'Playfair Display', serif; font-size: clamp(30px, 4vw, 46px); font-weight: 700; color: var(--text-primary); line-height: 1.12; letter-spacing: -0.5px; margin-bottom: 14px; }
        .ta-heading em { font-style: italic; color: var(--accent); }
        .ta-subheading { font-size: 15px; color: var(--text-secondary); font-weight: 300; line-height: 1.65; max-width: 500px; }
        .ta-hero-stats { display: flex; gap: 28px; margin-top: 32px; flex-wrap: wrap; }
        .ta-stat { display: flex; flex-direction: column; gap: 3px; }
        .ta-stat-num { font-family: 'Playfair Display', serif; font-size: 30px; font-weight: 700; color: var(--text-primary); line-height: 1; }
        .ta-stat-label { font-size: 12px; color: var(--text-muted); letter-spacing: 0.3px; }
        .ta-stat-divider { width: 1px; background: var(--border); }

        .ta-btn-create { display: inline-flex; align-items: center; gap: 8px; background: var(--accent); color: #fff; border: none; padding: 13px 26px; border-radius: 7px; font-family: 'Lato', sans-serif; font-size: 14px; font-weight: 700; cursor: pointer; letter-spacing: 0.3px; transition: background .2s, transform .15s, box-shadow .2s; box-shadow: 0 2px 14px rgba(124,92,58,.28); white-space: nowrap; }
        .ta-btn-create:hover { background: var(--accent-hover); transform: translateY(-1px); box-shadow: 0 5px 20px rgba(124,92,58,.38); }

        /* CONTENT */
        .ta-content { max-width: 1160px; margin: 0 auto; padding: 36px 40px; }

        /* API ERROR */
        .ta-api-error { background: var(--danger-bg); border: 1px solid #f5c6c6; border-radius: 8px; padding: 16px 20px; margin-bottom: 24px; display: flex; align-items: center; gap: 12px; font-size: 14px; color: var(--danger); }
        .ta-api-error button { margin-left: auto; background: var(--danger); color: #fff; border: none; padding: 7px 16px; border-radius: 5px; font-size: 12px; font-family: 'Lato', sans-serif; font-weight: 700; cursor: pointer; white-space: nowrap; flex-shrink: 0; }

        /* CARD */
        .ta-card { background: var(--surface); border: 1px solid var(--border); border-radius: 12px; overflow: hidden; box-shadow: var(--shadow-sm); }
        .ta-card-header { padding: 18px 24px; border-bottom: 1px solid var(--border-soft); display: flex; align-items: center; justify-content: space-between; }
        .ta-card-title { font-size: 13px; font-weight: 700; color: var(--text-secondary); letter-spacing: 0.5px; text-transform: uppercase; }
        .ta-badge { background: var(--accent-light); color: var(--accent); font-size: 11px; font-weight: 700; padding: 4px 11px; border-radius: 20px; }

        /* TABLE */
        .ta-table { width: 100%; border-collapse: collapse; }
        .ta-table thead tr { background: #faf8f5; }
        .ta-table th { padding: 13px 20px; text-align: left; font-size: 11px; text-transform: uppercase; letter-spacing: 1.2px; color: var(--text-muted); font-weight: 700; border-bottom: 1px solid var(--border-soft); }
        .ta-table tbody tr { border-bottom: 1px solid var(--border-soft); transition: background .12s; }
        .ta-table tbody tr:last-child { border-bottom: none; }
        .ta-table tbody tr:hover { background: #fdf9f6; }
        .ta-table td { padding: 16px 20px; vertical-align: middle; }

        .ta-avatar-cell { display: flex; align-items: center; gap: 13px; }
        .ta-avatar { width: 44px; height: 44px; border-radius: 50%; object-fit: cover; border: 2px solid var(--border); flex-shrink: 0; background: var(--border-soft); }
        .ta-avatar-placeholder { width: 44px; height: 44px; border-radius: 50%; background: var(--accent-light); display: flex; align-items: center; justify-content: center; flex-shrink: 0; color: var(--accent); font-size: 16px; font-weight: 700; border: 2px solid var(--border); }
        .ta-name { font-weight: 700; color: var(--text-primary); font-size: 14px; }
        .ta-loc { font-size: 12px; color: var(--text-muted); margin-top: 2px; }
        .ta-quote { max-width: 360px; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; line-height: 1.55; font-size: 13px; color: var(--text-secondary); font-style: italic; }
        .ta-stars { color: var(--gold); font-size: 14px; letter-spacing: 1px; }
        .ta-date { font-size: 12px; color: var(--text-muted); white-space: nowrap; }
        .ta-meta-row { display: flex; gap: 8px; flex-wrap: wrap; margin-top: 6px; }
        .ta-mini-badge { font-size: 10px; font-weight: 700; padding: 4px 8px; border-radius: 999px; text-transform: uppercase; letter-spacing: 0.7px; }
        .ta-mini-badge.live { background: #ecf8f1; color: #1f8f56; }
        .ta-mini-badge.hidden { background: #f4f1ec; color: #8b7658; }
        .ta-mini-badge.order { background: var(--accent-light); color: var(--accent); }
        .ta-actions { display: flex; gap: 6px; justify-content: flex-end; }
        .ta-btn-icon { background: none; border: 1px solid var(--border); border-radius: 6px; width: 34px; height: 34px; display: flex; align-items: center; justify-content: center; cursor: pointer; transition: all .15s; color: var(--text-muted); }
        .ta-btn-icon:hover.edit { border-color: var(--accent); color: var(--accent); background: var(--accent-light); }
        .ta-btn-icon:hover.del { border-color: var(--danger); color: var(--danger); background: var(--danger-bg); }

        /* STATE */
        .ta-state { text-align: center; padding: 80px 20px; display: flex; flex-direction: column; align-items: center; gap: 12px; }
        .ta-state-icon { font-size: 44px; }
        .ta-state-title { font-family: 'Playfair Display', serif; font-size: 20px; color: var(--text-primary); }
        .ta-state-msg { font-size: 14px; color: var(--text-muted); max-width: 300px; line-height: 1.55; }
        .ta-spinner { width: 36px; height: 36px; border-radius: 50%; border: 3px solid var(--border); border-top-color: var(--accent); animation: spin .8s linear infinite; }
        @keyframes spin { to { transform: rotate(360deg); } }

        /* OVERLAY */
        .ta-overlay { position: fixed; inset: 0; background: rgba(28,26,22,.5); backdrop-filter: blur(6px); display: flex; align-items: center; justify-content: center; z-index: 1000; padding: 24px; animation: fadeIn .18s ease; }
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }

        /* FORM MODAL */
        .ta-modal { background: var(--surface); border: 1px solid var(--border); border-radius: 14px; width: 100%; max-width: 548px; animation: slideUp .22s ease; max-height: 92vh; overflow-y: auto; box-shadow: var(--shadow-lg); }
        @keyframes slideUp { from { opacity: 0; transform: translateY(22px); } to { opacity: 1; transform: translateY(0); } }
        .ta-modal-header { padding: 28px 32px 22px; border-bottom: 1px solid var(--border); display: flex; align-items: flex-start; justify-content: space-between; gap: 16px; }
        .ta-modal-title { font-family: 'Playfair Display', serif; font-size: 23px; font-weight: 700; color: var(--text-primary); }
        .ta-modal-subtitle { font-size: 13px; color: var(--text-muted); margin-top: 5px; font-weight: 300; }
        .ta-modal-close { background: none; border: 1px solid var(--border); border-radius: 6px; color: var(--text-muted); cursor: pointer; padding: 6px; display: flex; transition: all .15s; flex-shrink: 0; }
        .ta-modal-close:hover { border-color: var(--text-secondary); color: var(--text-primary); }
        .ta-modal-body { padding: 26px 32px; display: flex; flex-direction: column; gap: 20px; }
        .ta-row { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }
        .ta-field { display: flex; flex-direction: column; gap: 6px; }
        .ta-label { font-size: 12px; font-weight: 700; color: var(--text-secondary); letter-spacing: 0.3px; }
        .ta-label span { color: var(--danger); }
        .ta-input, .ta-textarea { background: var(--bg); border: 1.5px solid var(--border); border-radius: 6px; padding: 11px 14px; color: var(--text-primary); font-family: 'Lato', sans-serif; font-size: 14px; outline: none; transition: border-color .15s, box-shadow .15s; width: 100%; }
        .ta-input:focus, .ta-textarea:focus { border-color: var(--accent); box-shadow: 0 0 0 3px rgba(124,92,58,.1); background: #fff; }
        .ta-input.err, .ta-textarea.err { border-color: var(--danger); }
        .ta-input::placeholder, .ta-textarea::placeholder { color: var(--text-muted); }
        .ta-textarea { resize: vertical; min-height: 96px; line-height: 1.55; }
        .ta-field-help { font-size: 12px; color: var(--text-muted); }
        .ta-error { font-size: 12px; color: var(--danger); }
        .ta-char { font-size: 12px; color: var(--text-muted); text-align: right; }
        .ta-char.over { color: var(--danger); }
        .ta-img-row { display: flex; align-items: center; gap: 14px; }
        .ta-img-preview { width: 52px; height: 52px; border-radius: 50%; object-fit: cover; border: 2px solid var(--border); flex-shrink: 0; }
        .ta-img-preview-lg { width: 88px; height: 88px; border-radius: 50%; object-fit: cover; border: 2px solid var(--border); }
        .ta-upload-actions { display: flex; align-items: center; gap: 12px; flex-wrap: wrap; }
        .ta-btn-remove-img { background: var(--danger-bg); border: 1px solid #f5c6c6; color: var(--danger); padding: 9px 14px; border-radius: 6px; font-family: 'Lato', sans-serif; font-size: 12px; font-weight: 700; cursor: pointer; }
        .ta-toggle { display: inline-flex; align-items: center; gap: 10px; font-size: 13px; color: var(--text-secondary); }
        .ta-toggle input { width: 16px; height: 16px; accent-color: var(--accent); }
        .ta-modal-footer { padding: 18px 32px 26px; display: flex; gap: 10px; justify-content: flex-end; border-top: 1px solid var(--border-soft); }
        .ta-btn-cancel { background: none; border: 1.5px solid var(--border); color: var(--text-secondary); padding: 11px 22px; border-radius: 6px; font-family: 'Lato', sans-serif; font-size: 14px; font-weight: 700; cursor: pointer; transition: all .15s; }
        .ta-btn-cancel:hover:not(:disabled) { border-color: var(--text-secondary); color: var(--text-primary); }
        .ta-btn-save { background: var(--accent); border: none; color: #fff; padding: 11px 28px; border-radius: 6px; font-family: 'Lato', sans-serif; font-size: 14px; font-weight: 700; cursor: pointer; transition: all .15s; display: flex; align-items: center; gap: 8px; box-shadow: 0 2px 10px rgba(124,92,58,.2); }
        .ta-btn-save:hover:not(:disabled) { background: var(--accent-hover); }
        .ta-btn-save:disabled { opacity: .6; cursor: not-allowed; }
        .ta-btn-spinner { width: 16px; height: 16px; border-radius: 50%; border: 2px solid rgba(255,255,255,.4); border-top-color: #fff; animation: spin .7s linear infinite; }

        /* DELETE MODAL */
        .ta-delete-modal { background: var(--surface); border: 1px solid var(--border); border-radius: 14px; width: 100%; max-width: 400px; padding: 38px 32px; text-align: center; animation: slideUp .2s ease; box-shadow: var(--shadow-lg); }
        .ta-delete-icon { font-size: 42px; margin-bottom: 16px; }
        .ta-delete-title { font-family: 'Playfair Display', serif; font-size: 22px; font-weight: 700; color: var(--text-primary); margin-bottom: 10px; }
        .ta-delete-msg { font-size: 14px; color: var(--text-secondary); line-height: 1.65; margin-bottom: 28px; }
        .ta-delete-name { color: var(--accent); font-weight: 700; }
        .ta-delete-actions { display: flex; gap: 10px; justify-content: center; }
        .ta-btn-del { background: var(--danger); border: none; color: #fff; padding: 11px 24px; border-radius: 6px; font-family: 'Lato', sans-serif; font-size: 14px; font-weight: 700; cursor: pointer; transition: background .15s; }
        .ta-btn-del:hover { background: #9c3030; }

        /* TOAST */
        .ta-toast { position: fixed; bottom: 28px; right: 28px; z-index: 2000; padding: 14px 20px; border-radius: 8px; font-size: 14px; font-weight: 700; display: flex; align-items: center; gap: 10px; animation: toastIn .25s ease; box-shadow: var(--shadow-md); font-family: 'Lato', sans-serif; }
        @keyframes toastIn { from { opacity: 0; transform: translateY(12px); } to { opacity: 1; transform: translateY(0); } }
        .ta-toast.success { background: var(--success); color: #fff; }
        .ta-toast.error { background: var(--danger); color: #fff; }

        @media (max-width: 768px) {
          .ta-hero, .ta-content { padding-left: 20px; padding-right: 20px; }
          .ta-topbar { padding: 0 20px; }
          .ta-table thead th:nth-child(4), .ta-table tbody td:nth-child(4) { display: none; }
          .ta-row { grid-template-columns: 1fr; }
          .ta-modal-body, .ta-modal-header, .ta-modal-footer { padding-left: 20px; padding-right: 20px; }
        }
        @media (max-width: 520px) {
          .ta-table thead th:nth-child(3), .ta-table tbody td:nth-child(3) { display: none; }
          .ta-quote { max-width: 180px; }
        }
      `}</style>

      <div className="ta-page">

        {/* TOP BAR */}
        <div className="ta-topbar">
          <span className="ta-topbar-logo">Hatha Yog</span>
          <span className="ta-topbar-sep">›</span>
          <span className="ta-topbar-page">Admin</span>
          <span className="ta-topbar-sep">›</span>
          <span className="ta-topbar-page">Testimonials</span>
        </div>

        {/* HERO HEADER */}
        <div className="ta-hero">
          <div className="ta-hero-inner">
            <button className="ta-btn-create" onClick={handleCreate}>
              <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
              </svg>
              Add Testimonial
            </button>
          </div>
        </div>

        {/* CONTENT */}
        <div className="ta-content">

          {apiError && (
            <div className="ta-api-error">
              <span>⚠️</span>
              <span>{apiError}</span>
              <button onClick={fetchAll}>Retry</button>
            </div>
          )}

          <div className="ta-card">
            <div className="ta-card-header">
              <span className="ta-card-title">All Testimonials</span>
              {!loading && <span className="ta-badge">{testimonials.length} records</span>}
               <span className="ta-badge">⭐ {averageRating} avg</span>
            </div>
            {loading ? (
              <div className="ta-state">
                <div className="ta-spinner" />
                <span className="ta-state-msg">Fetching testimonials from server…</span>
              </div>
            ) : testimonials.length === 0 && !apiError ? (
              <div className="ta-state">
                <div className="ta-state-icon">🪷</div>
                <div className="ta-state-title">No testimonials yet</div>
                <div className="ta-state-msg">Click "Add Testimonial" to publish your first community story to the homepage.</div>
              </div>
            ) : (
              <table className="ta-table">
                <thead>
                  <tr>
                    <th>User</th>
                    <th>Quote</th>
                    <th>Rating</th>
                    <th>Added On</th>
                    <th style={{ textAlign: 'right' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {testimonials.map(item => (
                    <tr key={item._id}>
                      <td>
                        <div className="ta-avatar-cell">
                          {item.image
                            ? <img src={resolveAssetUrl(item.image)} alt={item.name} className="ta-avatar" onError={e => e.target.style.display = 'none'} />
                            : <div className="ta-avatar-placeholder">{item.name?.[0]?.toUpperCase() || '?'}</div>
                          }
                          <div>
                            <div className="ta-name">{item.name}</div>
                            <div className="ta-loc">{item.location}</div>
                            <div className="ta-meta-row">
                              <span className={`ta-mini-badge ${item.isActive !== false ? 'live' : 'hidden'}`}>
                                {item.isActive !== false ? 'Live' : 'Hidden'}
                              </span>
                              <span className="ta-mini-badge order">Order {item.order ?? 0}</span>
                            </div>
                          </div>
                        </div>
                      </td>
                      <td><div className="ta-quote">"{item.quote}"</div></td>
                      <td><div className="ta-stars">{'★'.repeat(item.rating || 5)}</div></td>
                      <td>
                        <div className="ta-date">
                          {item.createdAt ? new Date(item.createdAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '—'}
                        </div>
                      </td>
                      <td>
                        <div className="ta-actions">
                          <button className="ta-btn-icon edit" title="Edit" onClick={() => handleEdit(item)}>
                            <svg width="15" height="15" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                              <path strokeLinecap="round" strokeLinejoin="round" d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" />
                              <path strokeLinecap="round" strokeLinejoin="round" d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" />
                            </svg>
                          </button>
                          <button className="ta-btn-icon del" title="Delete" onClick={() => setDeleteConfirm(item)}>
                            <svg width="15" height="15" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                              <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6M1 7h22M8 7V5a2 2 0 012-2h4a2 2 0 012 2v2" />
                            </svg>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>

      {/* FORM MODAL */}
      {showForm && (
        <div className="ta-overlay" onClick={e => e.target === e.currentTarget && !saving && closeForm()}>
          <div className="ta-modal">
            <div className="ta-modal-header">
              <div>
                <div className="ta-modal-title">{editingId ? 'Edit Testimonial' : 'New Testimonial'}</div>
                <div className="ta-modal-subtitle">{editingId ? 'Update the details below and save.' : 'Fill in the details to add a new community voice.'}</div>
              </div>
              <button className="ta-modal-close" onClick={() => !saving && closeForm()}>
                <svg width="18" height="18" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="ta-modal-body">
              <div className="ta-row">
                <div className="ta-field">
                  <label className="ta-label">Full Name <span>*</span></label>
                  <input className={`ta-input${errors.name ? ' err' : ''}`} placeholder="e.g. Ananya Sharma" value={formData.name} onChange={e => handleChange('name', e.target.value)} />
                  {errors.name && <span className="ta-error">{errors.name}</span>}
                </div>
                <div className="ta-field">
                  <label className="ta-label">Location <span>*</span></label>
                  <input className={`ta-input${errors.location ? ' err' : ''}`} placeholder="e.g. Delhi, India" value={formData.location} onChange={e => handleChange('location', e.target.value)} />
                  {errors.location && <span className="ta-error">{errors.location}</span>}
                </div>
              </div>

              <div className="ta-field">
                <label className="ta-label">Profile Image <span style={{ color: 'var(--text-muted)', fontWeight: 300 }}>(optional)</span></label>
                <div className="ta-upload-actions">
                  {imagePreview ? (
                    <img src={imagePreview} alt="preview" className="ta-img-preview-lg" />
                  ) : (
                    <div className="ta-avatar-placeholder" style={{ width: 88, height: 88, fontSize: 26 }}>
                      {formData.name?.[0]?.toUpperCase() || '?'}
                    </div>
                  )}
                  <div style={{ flex: 1, minWidth: 220 }}>
                    <input
                      type="file"
                      className="ta-input"
                      accept="image/*"
                      onChange={(e) => handleFileChange(e.target.files?.[0])}
                    />
                    <span className="ta-field-help">Upload a square portrait for the homepage slider</span>
                  </div>
                  {imagePreview && (
                    <button
                      type="button"
                      className="ta-btn-remove-img"
                      onClick={() => {
                        setFormData((prev) => ({ ...prev, image: null }));
                        setImagePreview('');
                        setRemoveImage(true);
                      }}
                    >
                      Remove Image
                    </button>
                  )}
                </div>
              </div>

              <div className="ta-row">
                <div className="ta-field">
                  <label className="ta-label">Rating <span>*</span></label>
                  <select
                    className={`ta-input${errors.rating ? ' err' : ''}`}
                    value={formData.rating}
                    onChange={(e) => handleChange('rating', Number(e.target.value))}
                  >
                    {[5, 4, 3, 2, 1].map((value) => (
                      <option key={value} value={value}>{value} Star{value > 1 ? 's' : ''}</option>
                    ))}
                  </select>
                  {errors.rating && <span className="ta-error">{errors.rating}</span>}
                </div>
                <div className="ta-field">
                  <label className="ta-label">Display Order</label>
                  <input
                    type="number"
                    min="0"
                    className="ta-input"
                    value={formData.order}
                    onChange={(e) => handleChange('order', Number(e.target.value))}
                  />
                  <span className="ta-field-help">Lower numbers appear first</span>
                </div>
              </div>

              <div className="ta-field">
                <label className="ta-label">Testimonial Quote <span>*</span></label>
                <textarea className={`ta-textarea${errors.quote ? ' err' : ''}`} placeholder="What did they say about Hatha Yog? Keep it authentic and heartfelt." value={formData.quote} onChange={e => handleChange('quote', e.target.value)} />
                <div className={`ta-char${formData.quote.length > 200 ? ' over' : ''}`}>{formData.quote.length} / 200</div>
                {errors.quote && <span className="ta-error">{errors.quote}</span>}
              </div>

              <div className="ta-field">
                <label className="ta-label">Homepage Visibility</label>
                <label className="ta-toggle">
                  <input
                    type="checkbox"
                    checked={formData.isActive}
                    onChange={(e) => handleChange('isActive', e.target.checked)}
                  />
                  Show this testimonial on the homepage
                </label>
              </div>
            </div>

            <div className="ta-modal-footer">
              <button className="ta-btn-cancel" onClick={() => !saving && closeForm()} disabled={saving}>Cancel</button>
              <button className="ta-btn-save" onClick={handleSave} disabled={saving}>
                {saving ? <><div className="ta-btn-spinner" />Saving…</> : (editingId ? 'Save Changes' : 'Add Testimonial')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* DELETE CONFIRM */}
      {deleteConfirm && (
        <div className="ta-overlay" onClick={e => e.target === e.currentTarget && setDeleteConfirm(null)}>
          <div className="ta-delete-modal">
            <div className="ta-delete-icon">🗑️</div>
            <div className="ta-delete-title">Delete Testimonial</div>
            <div className="ta-delete-msg">
              Remove the testimonial by <span className="ta-delete-name">{deleteConfirm.name}</span>?
              This will permanently delete it from the database and the homepage.
            </div>
            <div className="ta-delete-actions">
              <button className="ta-btn-cancel" onClick={() => setDeleteConfirm(null)}>Cancel</button>
              <button className="ta-btn-del" onClick={handleDelete}>Yes, Delete</button>
            </div>
          </div>
        </div>
      )}

      {/* TOAST */}
      {toast && <div className={`ta-toast ${toast.type}`}>{toast.type === 'success' ? '✓' : '✕'} {toast.msg}</div>}
    </>
  );
}
